import React from 'react'
import {
  BookOpen,
  Calculator,
  Clock,
  FileText,
  Landmark,
  MapPin,
  Receipt,
  ShieldAlert,
  Users,
  Wallet,
} from 'lucide-react'

const sections = [
  { id: 'overview', label: 'Overview' },
  { id: 'who-does-what', label: 'Who calculates vs who files' },
  { id: 'employee-setup', label: 'Employee setup' },
  { id: 'gross-pay', label: 'Gross pay' },
  { id: 'withholding', label: 'Employee withholding' },
  { id: 'employer-tax', label: 'Employer taxes' },
  { id: 'run-lifecycle', label: 'Payroll run lifecycle' },
  { id: 'tax-surfaces', label: 'Tax page, TaxJar, TaxBandits' },
  { id: 'ops', label: 'Platform ops' },
  { id: 'limits', label: 'Limits' },
]

const PayrollDocumentationPage: React.FC = () => {
  return (
    <div className="pb-16">
      <div className="mb-8">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-primary-100 p-2 text-primary-800">
            <BookOpen className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Payroll calculation & features</h1>
            <p className="mt-1 text-sm text-gray-500">
              How ServiceFlow Accounting computes pay stubs, remits employer tax, and e-files. Lives
              in the Accounting portal — this page is the operator reference.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[220px_minmax(0,1fr)]">
        <nav className="lg:sticky lg:top-6 lg:self-start">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">On this page</p>
          <ul className="space-y-1 text-sm">
            {sections.map((section) => (
              <li key={section.id}>
                <a
                  href={`#${section.id}`}
                  className="block rounded-md px-2 py-1 text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                >
                  {section.label}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <div className="space-y-10">
          <section id="overview" className="scroll-mt-6">
            <h2 className="text-lg font-semibold text-gray-900">Overview</h2>
            <p className="mt-2 text-sm leading-6 text-gray-600">
              Payroll runs in <strong>Accounting</strong> (Payroll → Employees / Runs, plus Tax).
              A run takes approved time-clock hours, employee W-4 and work location, then writes
              federal, state, FICA, and Local/SDI on each stub. Paid stubs feed employer liability
              and TaxBandits e-file. Recalculate on a <em>draft</em> run only — paid history is not
              rewritten.
            </p>
            <div className="mt-4 grid gap-3 sm:grid-cols-3">
              <FeatureCard
                icon={Users}
                title="Employees"
                body="W-4 2020+ fields, home address, optional work state/ZIP, pay rate, and bank for ACH."
              />
              <FeatureCard
                icon={Calculator}
                title="Stub math"
                body="Pub 15-T federal + PayrollTax API rates for state/FICA/SDI/local. Static tables if the API is down."
              />
              <FeatureCard
                icon={Landmark}
                title="File & remit"
                body="TaxBandits files W-2 / 941 / 940 / 1099 from amounts we already computed. It does not calculate checks."
              />
            </div>
          </section>

          <section id="who-does-what" className="scroll-mt-6">
            <h2 className="text-lg font-semibold text-gray-900">Who calculates vs who files</h2>
            <div className="mt-3 overflow-hidden rounded-lg border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium text-gray-700">System</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-700">Role</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white text-gray-600">
                  <tr>
                    <td className="px-4 py-2 font-medium text-gray-900">Accounting Recalculate</td>
                    <td className="px-4 py-2">
                      Source of truth for stub dollars: gross, FIT, SIT, FICA, Local/SDI, net.
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 font-medium text-gray-900">PayrollTax API</td>
                    <td className="px-4 py-2">
                      Live federal/state/FICA brackets and SDI/SUTA/local rows when{' '}
                      <code className="rounded bg-gray-100 px-1 text-xs">PAYROLLTAXAPI_API_KEY</code> is set.
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 font-medium text-gray-900">IRS Pub 15-T (2026)</td>
                    <td className="px-4 py-2">
                      Federal income-tax withholding (percentage method). Works offline without the API.
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 font-medium text-gray-900">TaxJar</td>
                    <td className="px-4 py-2">Invoice <em>sales</em> tax only. Not used on paychecks.</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 font-medium text-gray-900">TaxBandits</td>
                    <td className="px-4 py-2">
                      E-file W-2, 941, 940, 1099 from our computed amounts. Requires a real EIN (not 123456789).
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 font-medium text-gray-900">Tax Management grid</td>
                    <td className="px-4 py-2">
                      Liability tracking / reference. Those percentages are <strong>not</strong> used on Recalculate.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section id="employee-setup" className="scroll-mt-6">
            <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
              <MapPin className="h-5 w-5 text-gray-400" />
              Employee setup
            </h2>
            <ul className="mt-3 list-disc space-y-2 pl-5 text-sm leading-6 text-gray-600">
              <li>
                <strong>Home address</strong> (street, city, 2-letter state, ZIP) is used for W-2.
              </li>
              <li>
                <strong>Work state / work ZIP</strong> drive withholding lookup. Leave blank to use home.
                If home and work states differ, Accounting sends <code className="rounded bg-gray-100 px-1 text-xs">residenceState</code> for reciprocity.
              </li>
              <li>
                <strong>W-4 (2020+)</strong>: filing status, Step 2(c) multiple jobs, Step 3 dependents,
                Step 4(a) other income, 4(b) deductions, 4(c) extra withholding, federal/state/FICA exemptions.
              </li>
              <li>
                Missing work/home state is a <em>warning</em> on Recalculate, not a hard block. State tax
                will be approximate until a 2-letter code is set.
              </li>
              <li>
                Work ZIP is stored for future local lookup. The rate API is state + pay date today — we do
                not invent NYC / Ohio / PA EIT from state-only data.
              </li>
            </ul>
          </section>

          <section id="gross-pay" className="scroll-mt-6">
            <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
              <Clock className="h-5 w-5 text-gray-400" />
              Gross pay
            </h2>
            <ul className="mt-3 list-disc space-y-2 pl-5 text-sm leading-6 text-gray-600">
              <li>
                <strong>Hourly</strong>: only approved ServiceFlow time-clock punches with an empty job id.
                Job-tied time is labor cost, not wages. Overtime is 1.5×.
              </li>
              <li>
                <strong>Salary</strong>: pay rate ÷ periods per year (weekly 52, biweekly 26, semimonthly 24,
                monthly 12).
              </li>
              <li>Bonus and commission on the stub add to gross before tax.</li>
              <li>
                Pretax deductions (401k, etc.) reduce <em>taxable</em> wages. FICA year-to-date uses prior
                paid stubs&apos; <code className="rounded bg-gray-100 px-1 text-xs">gross − pretax</code>, not raw gross.
              </li>
            </ul>
          </section>

          <section id="withholding" className="scroll-mt-6">
            <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
              <Wallet className="h-5 w-5 text-gray-400" />
              Employee withholding
            </h2>
            <div className="mt-3 space-y-4 text-sm leading-6 text-gray-600">
              <div className="rounded-lg border border-gray-200 bg-white p-4">
                <h3 className="font-medium text-gray-900">Federal income tax — Pub 15-T (2026)</h3>
                <p className="mt-1">
                  Automated percentage method (Worksheet 1A). Annualizes taxable wages, applies Step 2
                  checkbox tables when multiple jobs is checked, subtracts the Step 3 credit, then adds
                  Step 4(c) extra withholding. If filing status has no table, Accounting falls back to
                  annualized brackets so the run does not fail.
                </p>
              </div>
              <div className="rounded-lg border border-gray-200 bg-white p-4">
                <h3 className="font-medium text-gray-900">State income tax</h3>
                <p className="mt-1">
                  Graduated brackets or a flat rate from PayrollTax API for the work state. If the API is
                  unset or fails, built-in static state rates are used (0% for no-tax states such as FL/TX).
                </p>
              </div>
              <div className="rounded-lg border border-gray-200 bg-white p-4">
                <h3 className="font-medium text-gray-900">FICA</h3>
                <p className="mt-1">
                  Social Security and Medicare from API rates and wage base (static fallback uses the
                  current SSA wage base). Additional Medicare applies after the statutory threshold.
                </p>
              </div>
              <div className="rounded-lg border border-gray-200 bg-white p-4">
                <h3 className="font-medium text-gray-900">Local / SDI</h3>
                <p className="mt-1">
                  Employee-side SDI, PFML, and local income rows from the API are written to{' '}
                  <code className="rounded bg-gray-100 px-1 text-xs">localTax</code>, included in total
                  tax and net pay, and shown as <strong>Local / SDI</strong> on the stub grid.
                </p>
              </div>
            </div>
          </section>

          <section id="employer-tax" className="scroll-mt-6">
            <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
              <Receipt className="h-5 w-5 text-gray-400" />
              Employer taxes
            </h2>
            <ul className="mt-3 list-disc space-y-2 pl-5 text-sm leading-6 text-gray-600">
              <li>Employer FICA is matched 1:1 to employee Social Security + Medicare on paid stubs.</li>
              <li>
                <strong>FUTA</strong> defaults to 0.6% on the first $7,000 per employee unless the API
                returns a FUTA row.
              </li>
              <li>
                <strong>SUTA</strong> is an estimate: API rate × wages (with wage base when present).
                Official experience-rate override is not a product field yet.
              </li>
            </ul>
          </section>

          <section id="run-lifecycle" className="scroll-mt-6">
            <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
              <FileText className="h-5 w-5 text-gray-400" />
              Payroll run lifecycle
            </h2>
            <ol className="mt-3 list-decimal space-y-2 pl-5 text-sm leading-6 text-gray-600">
              <li>
                <strong>Create (draft)</strong> — active employees in the period get stubs. Hourly hours
                come from approved clock punches; missing clock hours warn (and block process unless overridden).
              </li>
              <li>
                <strong>Recalculate</strong> — rebuilds stubs from current employees, hours, W-4, and tax
                tables. Logs <code className="rounded bg-gray-100 px-1 text-xs">source: payrolltaxapi</code> or{' '}
                <code className="rounded bg-gray-100 px-1 text-xs">static</code>.
              </li>
              <li>
                <strong>Approve</strong> — recalculates once more, then locks the run for processing.
              </li>
              <li>
                <strong>Process / pay</strong> — ACH via connected bank or mark check/manual paid. Posts
                wages, employee/employer FICA, and FIT/SIT/Local withholdings to the payroll GL accounts.
              </li>
              <li>
                <strong>Deposits & e-file</strong> — record EFTPS (or state) deposits on Tax, then file
                W-2 / 941 / 940 / 1099 through TaxBandits.
              </li>
            </ol>
          </section>

          <section id="tax-surfaces" className="scroll-mt-6">
            <h2 className="text-lg font-semibold text-gray-900">Tax page, TaxJar, TaxBandits</h2>
            <p className="mt-2 text-sm leading-6 text-gray-600">
              Initialize Tax Data seeds only labeled statutory FICA/FUTA reference rows — not fake
              Florida sales tax or a 22% federal income rate. Enable TaxJar per division for invoice
              sales tax. Connect TaxBandits with a real EIN and complete employer profile (business
              type, kind of employer, signing authority, state tax account IDs) before filing.
            </p>
          </section>

          <section id="ops" className="scroll-mt-6">
            <h2 className="text-lg font-semibold text-gray-900">Platform ops</h2>
            <ul className="mt-3 list-disc space-y-2 pl-5 text-sm leading-6 text-gray-600">
              <li>
                Secret <code className="rounded bg-gray-100 px-1 text-xs">PAYROLLTAXAPI_API_KEY</code> is
                mapped on accounting-service deploy. Without it, stubs still withhold federal via Pub 15-T
                and use static state/FICA tables.
              </li>
              <li>
                Optional: <code className="rounded bg-gray-100 px-1 text-xs">PAYROLLTAXAPI_USE_STATIC=true</code> forces
                static rates even when a key is present.
              </li>
              <li>
                Payroll Tax add-on ($79/mo) gates TaxBandits e-file. Local/dev can use{' '}
                <code className="rounded bg-gray-100 px-1 text-xs">PAYROLL_TAX_DEV_UNLOCK</code> (ignored in production).
              </li>
              <li>Deploy accounting-service (and its client) after payroll-tax code changes.</li>
            </ul>
          </section>

          <section id="limits" className="scroll-mt-6">
            <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
              <ShieldAlert className="h-5 w-5 text-amber-500" />
              Limits
            </h2>
            <ul className="mt-3 list-disc space-y-2 pl-5 text-sm leading-6 text-gray-600">
              <li>TaxBandits never calculates withholdings — it only files what Recalculate stored.</li>
              <li>No official SUTA experience-rate field yet (API estimate only).</li>
              <li>Locals appear only when PayrollTax returns an employee city/county/SDI row.</li>
              <li>Changing tax logic does not rewrite historical paid stubs; Recalculate draft runs only.</li>
            </ul>
          </section>
        </div>
      </div>
    </div>
  )
}

function FeatureCard({
  icon: Icon,
  title,
  body,
}: {
  icon: React.ComponentType<{ className?: string }>
  title: string
  body: string
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <Icon className="h-5 w-5 text-primary-600" />
      <h3 className="mt-2 text-sm font-semibold text-gray-900">{title}</h3>
      <p className="mt-1 text-sm text-gray-600">{body}</p>
    </div>
  )
}

export default PayrollDocumentationPage
